import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import type { ArticleMeta } from '@/mocks/articlesData';

interface ArticleLayoutProps {
  data: ArticleMeta;
  children: React.ReactNode;
}

const siteUrl = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
  ? import.meta.env.VITE_SITE_URL
  : 'https://sugargoosheet.cam';

function buildArticleJsonLd(data: ArticleMeta) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    description: data.excerpt,
    image: data.headerImage,
    datePublished: '2026-08-01',
    dateModified: '2026-08-09',
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet Team',
      url: siteUrl + '/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet',
      url: siteUrl + '/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': siteUrl + '/' + data.slug,
    },
  };
}

export default function ArticleLayout({ data, children }: ArticleLayoutProps) {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(data)) }}
      />
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link
                  to="/"
                  className="hover:text-primary-500 transition-colors duration-200"
                >
                  Home
                </Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              </li>
              <li className="text-foreground-700 font-medium">{data.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
              {data.category} · {data.date}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {data.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {data.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-5 pt-5 border-t border-background-200/60">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                </div>
                <div>
                  <p className="text-xs font-medium text-foreground-800">
                    Sugargoo Sheet Team
                  </p>
                  <p className="text-[10px] text-foreground-500">
                    Updated: {data.date}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
                <span>{data.readTime}</span>
              </div>
            </div>
          </header>

          {data.headerImage && (
            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={data.headerImage}
                alt={data.title}
                className="w-full h-56 md:h-80 object-cover"
              />
            </div>
          )}

          <div className="prose-content">{children}</div>

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">
              Continue Reading
            </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-100 text-foreground-800 text-sm font-semibold rounded-full hover:bg-background-200 border border-background-200/60 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Explore Sugargoo Spreadsheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}